/**
 * Manettes en pixel art, dessinées à partir de petites grilles de caractères (un caractère = un pixel).
 *
 *   .  vide            o  contour          k  fond sombre (croix, sticks, écran)    w  blanc
 *   b  corps           h  corps clair      g  corps ombré
 *   a  bouton vert     c  bouton rouge     d  bouton bleu      e  bouton jaune
 *
 * Les couleurs du corps (b, h, g) changent d'une manette à l'autre (variables CSS du composant).
 * Dessins originaux, volontairement génériques : aucune marque ni aucun logo de console.
 */

/** Moitié gauche (13 colonnes) du corps de la manette moderne ; l'autre moitié est son miroir. */
const MODERNE_MOITIE = [
    "....oooooo...",
    "...obbbbbbo..",
    "..ooooooooooo",
    ".ohhhhhhhhhhh",
    "obbbbbbbbbbbb",
    "obbbbbbbbbbbb",
    "obbbbbbbbbbbb",
    "obbbbbbbbbbbb",
    "obbbbbbbbbbbb",
    "obbbbbbbooooo",
    "obbbbbbo.....",
    "ogggggo......",
    "ogggggo......",
    ".oooooo......",
];

const moitieVersGrille = moitie => moitie.map(ligne => [...(ligne + [...ligne].reverse().join(""))]);

/** Pose des pixels (x, y, caractère) sur une grille. */
function poser(grille, pixels) {
    pixels.forEach(([x, y, c]) => { grille[y][x] = c; });
    return grille;
}

const MODERNE = poser(moitieVersGrille(MODERNE_MOITIE), [
    // croix directionnelle
    [6, 5, "k"], [5, 6, "k"], [6, 6, "k"], [7, 6, "k"], [6, 7, "k"],
    // sticks
    [9, 7, "k"], [10, 7, "k"], [9, 8, "k"], [10, 8, "k"],
    [16, 7, "k"], [15, 7, "k"], [16, 8, "k"], [15, 8, "k"],
    // boutons du centre
    [12, 4, "w"], [13, 4, "w"],
    // boutons d'action
    [19, 4, "e"], [17, 5, "d"], [21, 5, "c"], [19, 6, "a"],
]);

const RETRO = poser([
    "oooooooooooooooooooooooo",
    "obbbbbbbbbbbbbbbbbbbbbbo",
    ...Array.from({length: 8}, () => "obkkkkkkkkkkkkkkkkkkkkbo"),
    "obbbbbbbbbbbbbbbbbbbbbbo",
    "oooooooooooooooooooooooo",
].map(ligne => [...ligne]), [
    // croix
    [5, 4, "w"], [4, 5, "w"], [5, 5, "w"], [6, 5, "w"], [5, 6, "w"],
    // select et start
    [9, 6, "b"], [10, 6, "b"], [13, 6, "b"], [14, 6, "b"],
    // deux boutons rouges
    [16, 5, "c"], [17, 5, "c"], [16, 6, "c"], [17, 6, "c"],
    [20, 5, "c"], [21, 5, "c"], [20, 6, "c"], [21, 6, "c"],
]);

export const GRILLES = {moderne: MODERNE, retro: RETRO};

/**
 * Transforme une grille en tracés SVG : un seul tracé par couleur, fait de rectangles d'un pixel de haut
 * (les pixels voisins d'une même ligne sont regroupés). Rend [{caractere, d}], le vide exclu.
 */
export function tracer(grille) {
    const parCouleur = {};
    grille.forEach((ligne, y) => {
        let x = 0;
        while (x < ligne.length) {
            const c = ligne[x];
            let fin = x;
            while (fin + 1 < ligne.length && ligne[fin + 1] === c) fin++;
            if (c !== ".") (parCouleur[c] = parCouleur[c] || []).push(`M${x} ${y}h${fin - x + 1}v1h${-(fin - x + 1)}z`);
            x = fin + 1;
        }
    });
    return Object.keys(parCouleur).map(caractere => ({caractere, d: parCouleur[caractere].join("")}));
}

/** Tracés prêts à l'emploi, calculés une fois : {moderne: {largeur, hauteur, traces}, retro: {...}} */
export const SPRITES = Object.keys(GRILLES).reduce((acc, nom) => {
    const grille = GRILLES[nom];
    acc[nom] = {largeur: grille[0].length, hauteur: grille.length, traces: tracer(grille)};
    return acc;
}, {});
